import { Redo, Trash2, Undo } from 'lucide-react';
import {
  useAnnotationDraft,
  useAnnotationStrokeColor,
  useAnnotationTool,
} from '../../model/comments.store';
import type { AnnotatedCommentMediaKind, AnnotationTool } from '../../model/annotation';
import { BASE_TOOL_SPECS, PDF_TOOL_SPECS } from '../../lib/toolSpecs';
import { IconActionButton } from '../IconActionButton';
import { ColorRow } from './ColorRow';
import { useTranslation } from 'react-i18next';

interface Props {
  assetKind: AnnotatedCommentMediaKind;
  /**
   * When true, the popover includes the undo / redo / clear actions row
   * (used by the edit composer, which moves these out of the inline
   * actions bar). When false, the actions only render inline at the
   * composer level — see `CommentInputActionsBar.showDraftActions`.
   */
  showDraftActions?: boolean;
}

export function AnnotationToolbar({ assetKind, showDraftActions = false }: Props) {
  const { t } = useTranslation('projects');

  const { tool, selectTool } = useAnnotationTool();
  const { strokeColor, setStrokeColor } = useAnnotationStrokeColor();
  const { draftShapes, redoStack, undoDraft, redoDraft, clearDrafts } = useAnnotationDraft();

  const onToolClick = (next: AnnotationTool) => {
    selectTool(tool === next ? null : next);
  };

  const tools = assetKind === 'pdf' ? PDF_TOOL_SPECS : BASE_TOOL_SPECS;
  const hasDrafts = draftShapes.length > 0;
  const canRedo = redoStack.length > 0;

  return (
    <div className="flex w-fit flex-col gap-3 p-3">
      <div className="flex flex-col gap-1.5">
        <span className="text-sm text-muted-foreground">
          {t('commentOnFile.annotationToolbar.tool')}
        </span>
        <div className="flex items-center gap-1">
          {tools.map((spec) => (
            <IconActionButton
              key={spec.tool}
              label={spec.label}
              Icon={spec.Icon}
              active={tool === spec.tool}
              onClick={() => onToolClick(spec.tool)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm text-muted-foreground">
          {t('commentOnFile.annotationToolbar.color')}
        </span>
        <ColorRow strokeColor={strokeColor} onPick={setStrokeColor} />
      </div>

      {showDraftActions && hasDrafts && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm text-muted-foreground">
            {t('commentOnFile.annotationToolbar.actions')}
          </span>
          <div className="flex items-center gap-1">
            <IconActionButton
              label={t('commentOnFile.annotationToolbar.undo')}
              Icon={Undo}
              onClick={undoDraft}
            />
            <IconActionButton
              label={t('commentOnFile.annotationToolbar.redo')}
              Icon={Redo}
              onClick={redoDraft}
              disabled={!canRedo}
            />
            <IconActionButton
              label={t('commentOnFile.annotationToolbar.clear')}
              Icon={Trash2}
              onClick={clearDrafts}
            />
          </div>
        </div>
      )}
    </div>
  );
}
